import type { CaptureRow, CompanyLogoMime } from '../../data/database.types';
import type { CapturePhotoView } from '../../data/photos/photo-view';
import { CAPTURE_STATUS_LABEL, formatCaptureTitle } from '../captures/capture-status';
import { formatInstant } from '../../lib/timezone';

/**
 * WHAT THE CLIENT ACTUALLY RECEIVES, as data — M10 / S10-03B.
 *
 * **PURE, AND THAT IS THE POINT.** Every question about what appears in a
 * report is answered here, once, in a function a test can call without a
 * browser: is there a contact line, does this Capture have any Before photos,
 * is the Description present at all. A component that decided those things
 * while rendering would make each rule provable only by mounting a screen —
 * and would leave S10-04 re-deciding them for the PDF.
 *
 * **ABSENT MEANS ABSENT.** Nothing here emits an empty block, a placeholder or
 * a reserved row. A missing logo produces no logo; a Capture with no chosen
 * photos produces no photo group. That rule is Brandon's no-wasted-space
 * requirement expressed as data rather than as CSS, which is why it can be
 * tested by asserting a field is `null` rather than by measuring pixels.
 *
 * **NO CREWLINK ANYWHERE.** This is Brandon's company's document to his client.
 * The software that produced it does not sign its own work.
 */

export interface ReportBranding {
  readonly companyName: string;
  readonly logo: { readonly mime: CompanyLogoMime; readonly base64: string } | null;
  readonly email: string | null;
  readonly phone: string | null;
  /** Pre-joined: `email · phone`, one of them, or **null when there is neither**. */
  readonly contactLine: string | null;
}

export interface ReportPhoto {
  readonly key: string;
  readonly view: CapturePhotoView;
}

export interface ReportCaptureSection {
  readonly captureId: string;
  /** `CAP-0041 — Unit 903 Bedroom`, or the honest no-location fallback. */
  readonly title: string;
  /** `Aug 15, 2026 · ✓ REPAIRED` — the client-facing meta line. */
  readonly meta: string;
  readonly before: readonly ReportPhoto[];
  readonly after: readonly ReportPhoto[];
}

export interface ReportDocument {
  /** Null when branding is off, or was never configured. **No empty block.** */
  readonly branding: ReportBranding | null;
  readonly projectName: string;
  readonly reportDate: string;
  /** Null when blank. **No empty Description section.** */
  readonly description: string | null;
  readonly captures: readonly ReportCaptureSection[];
}

/** `email · phone` · one alone · **null when neither exists**. */
export function joinContact(email: string | null, phone: string | null): string | null {
  const parts = [email, phone].map((part) => part?.trim() ?? '').filter((part) => part.length > 0);
  return parts.length === 0 ? null : parts.join(' · ');
}

export interface BuildReportDocumentInput {
  readonly includeBranding: boolean;
  readonly branding: {
    readonly companyName: string;
    readonly logoMime: CompanyLogoMime | null;
    readonly logoBase64: string | null;
    readonly email: string | null;
    readonly phone: string | null;
  } | null;
  readonly projectName: string;
  /** The canonical Crewlink timezone. **Required** — no device fallback (INV-T17). */
  readonly timeZone: string;
  readonly now: Date;
  readonly description: string;
  readonly captures: readonly {
    readonly capture: CaptureRow;
    readonly locationDisplay: string | null;
    /** ONLY the photos Brandon chose. Unselected evidence never reaches here. */
    readonly selected: readonly CapturePhotoView[];
  }[];
}

export function buildReportDocument(input: BuildReportDocumentInput): ReportDocument {
  const branding =
    input.includeBranding && input.branding && input.branding.companyName.trim().length > 0
      ? {
          companyName: input.branding.companyName,
          logo:
            input.branding.logoMime && input.branding.logoBase64
              ? { mime: input.branding.logoMime, base64: input.branding.logoBase64 }
              : null,
          email: input.branding.email,
          phone: input.branding.phone,
          contactLine: joinContact(input.branding.email, input.branding.phone),
        }
      : null;

  return {
    branding,
    projectName: input.projectName,
    // The date the report is being made. S10-05 later freezes this as the
    // generation timestamp; one value maps into that cleanly.
    reportDate: formatInstant(input.now, input.timeZone, { dateStyle: 'medium' }),
    description: input.description.trim().length > 0 ? input.description : null,
    captures: input.captures.map(({ capture, locationDisplay, selected }) => ({
      captureId: capture.id,
      title: formatCaptureTitle(capture.capture_number, locationDisplay),
      meta: `${formatInstant(capture.created_at, input.timeZone, { dateStyle: 'medium' })} · ${
        CAPTURE_STATUS_LABEL[capture.status]
      }`,
      before: selected
        .filter((view) => view.photoClass === 'before')
        .map((view) => ({ key: view.key, view })),
      after: selected
        .filter((view) => view.photoClass === 'after')
        .map((view) => ({ key: view.key, view })),
    })),
  };
}

/** How many photos share a row on the page. Two keeps evidence legible at Letter width. */
/**
 * One atomic piece of the report.
 *
 * **ATOMIC MEANS "NEVER SPLIT INTERNALLY".** A section is the smallest thing
 * packing is allowed to move, so a photo can never be cut in half and a label
 * can never be separated from its picture — those relationships are decided
 * when sections are built, not while pages are being filled.
 */
export interface ReportSection {
  readonly id: string;
  /**
   * A section that must not be left alone at the foot of a page.
   *
   * A Capture heading with its evidence pushed to the next page is the classic
   * ugly orphan: the client sees a title, then nothing. When a keep-with
   * section cannot be followed by its next section on the same page, BOTH move
   * together.
   */
  readonly keepWithNext?: boolean;
}

export const PHOTOS_PER_ROW = 2;

/**
 * The document, flattened into the atomic pieces page packing may move.
 *
 * **PHOTO ROWS ARE THE SAFE INTERNAL BOUNDARY.** A Capture with fourteen photos
 * is taller than any page, so it has to divide somewhere; dividing between rows
 * of photos never cuts an image and never separates a picture from its label.
 * The heading is marked to travel with the first row, so a client never turns a
 * page to find a title standing over nothing.
 */
export function reportSections(document: ReportDocument): ReportSection[] {
  const sections: ReportSection[] = [];

  if (document.branding) sections.push({ id: 'branding' });
  sections.push({ id: 'project' });
  if (document.description) sections.push({ id: 'description' });

  for (const capture of document.captures) {
    const rows: string[] = [];
    for (const photoClass of ['before', 'after'] as const) {
      const photos = photoClass === 'before' ? capture.before : capture.after;
      // No photos of a class produces NO group — not an empty one.
      for (let start = 0; start < photos.length; start += PHOTOS_PER_ROW) {
        rows.push(`${capture.captureId}:${photoClass}:${start}`);
      }
    }

    sections.push({
      id: `${capture.captureId}:head`,
      // A Capture with no chosen photos has nothing to keep its heading with,
      // and that is fine: the heading IS the whole section.
      keepWithNext: rows.length > 0,
    });
    for (const row of rows) sections.push({ id: row });
  }

  return sections;
}
